import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

// Local components and styles
import HeaderItem from '../shared/HeaderItem';
import {
    Wrapper,
    LeftContentWrapper,
    RightContentWrapper,
    MiddleContentWrapper,
    LogoWrapper,
    IconWrapper,
    VersionWrapper,
    MobileMenuButton,
    MobileMenuOverlay,
    MobileMenuPanel,
    MobileMenuItem,
    MobileMenuDivider,
} from './PageHeader.styles';
import DisclaimerPopup from './DisclaimerPopup';

// Side panel components and styles
import SidePanelPopup, { SidePanelModulEnum } from '../common/side-panel-popup/SidePanelPopup';
import {
    userInfoPanelClass,
    logoutSectionClass,
    appsContentClass,
    userAccountSectionClass,
    userAccountNameClass,
    userAccountEmailClass,
    userRolesSectionClass,
    rolesContainerClass,
    roleItemContainerClass,
    roleNameSpanClass,
} from '../common/side-panel-popup/SidePanelPopup.styles';

// Authentication and API hooks
import useMsal from '../../auth/useMsal';
import { useAuthStore } from '../../context/AuthContext';
import { useEditMode } from '../../context/EditModeContext';
import { useAppsMenuStore } from '../../context/AppsMenuContext';
import { getUniqueRoleDisplayNames } from '../../utils/roleMapper';
import '../../utils/fontAwesomeSetup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faPencilAlt, faBox, faFileAlt, faWrench, faHistory, faUserShield, faTh, faUserCircle } from '@fortawesome/free-solid-svg-icons';

const PageHeader = () => {
    const [visiblePopup, setVisiblePopup] = useState<SidePanelModulEnum>(SidePanelModulEnum.NONE);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { logoutAzAd } = useMsal();
    const { loggedInUser, roles, canEdit, canEditChemGuard } = useAuthStore();
    const { isInEditMode, toggleEditMode } = useEditMode();
    const { appsMenu, isLoading: isAppsMenuLoading } = useAppsMenuStore();
    const navigate = useNavigate();
    const location = useLocation();

    const handleAboutClick = () => {
        setVisiblePopup(SidePanelModulEnum.About);
    };

    const handleMyAppsClick = () => {
        setVisiblePopup(SidePanelModulEnum.AppsMenu);
    };

    const handleClosePopup = () => {
        setVisiblePopup(SidePanelModulEnum.NONE);
    };

    const handleLogout = () => {
        logoutAzAd();
    };

    const handleProductsClick = () => {
        navigate('/products');
    };

    const handleDocumentsClick = () => {
        navigate('/documents');
    };

    const handleChangeLogClick = () => {
        navigate('/changelog');
    };

    const handleToolsClick = () => {
        navigate('/tools');
    };

    const handleAdminClick = () => {
        navigate('/admin');
    };

    const handleEditModeToggle = () => {
        toggleEditMode();
    };

    const handleMobileNav = (handler: () => void) => {
        setMobileMenuOpen(false);
        handler();
    };

    return (
        <Wrapper>
            <LeftContentWrapper>
                <LogoWrapper>
                    <Link to="/products">
                        <h1>
                            Commodity<span className="sub-header">Book</span>
                        </h1>
                    </Link>
                    <VersionWrapper>v1.0.0</VersionWrapper>
                </LogoWrapper>
                <DisclaimerPopup>
                    <h3>Disclaimer and Terms of Use</h3>
                    <p>The information and guidelines (the &quot;Information&quot;) in Odfjell Tankers Commodity Book are presented in good faith and are believed to be correct, but are given without guarantee. Odfjell Tankers makes no representations or warranties as to the completeness or accuracy of Information.</p>
                    <p>The Commodity Book is updated regularly as new products and information are received.</p>
                    <p>Each master/owner and terminal representative is ultimately responsible for the safety of any cargo operation onboard or at terminal. If Information is not in compliance with international/local regulations, then international/local regulations shall prevail.</p>
                    <p>Information is supplied upon the condition that the persons receiving same will make their own determination as to its suitability for their purposes prior to use. In no event shall Odfjell Tankers or related companies be responsible for damage, loss or expense of any nature whatsoever resulting from the use of, or reliance upon, Information or the product to which Information refers.</p>
                </DisclaimerPopup>
            </LeftContentWrapper>

            <MiddleContentWrapper>
            </MiddleContentWrapper>
            <RightContentWrapper>
                <IconWrapper>
                    {/* Edit Mode Button - Only visible for users with edit permissions */}
                    {(canEdit || canEditChemGuard) && (
                        <span style={{ marginRight: '3rem' }}>
                            <HeaderItem title="Edit" iconName="pencil-alt" isActive={isInEditMode} onItemClick={handleEditModeToggle} />
                        </span>
                    )}
                    <HeaderItem title="Products" iconName="box" isActive={location.pathname === '/products'} onItemClick={handleProductsClick} />
                    <HeaderItem title="Documents" iconName="file-alt" isActive={location.pathname === '/documents'} onItemClick={handleDocumentsClick} />
                    <HeaderItem title="Tools" iconName="wrench" isActive={location.pathname === '/tools'} onItemClick={handleToolsClick} />
                    {(canEdit || canEditChemGuard) && (
                        <HeaderItem
                            title="Change Log"
                            iconName="history"
                            isActive={location.pathname === '/changelog'}
                            onItemClick={handleChangeLogClick}
                        />
                    )}
                    {(canEdit || canEditChemGuard) && (
                        <HeaderItem title="Admin" iconName="user-shield" isActive={location.pathname === '/admin'} onItemClick={handleAdminClick} />
                    )}
                </IconWrapper>
                <IconWrapper>
                    <HeaderItem title="My apps" iconName="th" onItemClick={handleMyAppsClick} />
                    <HeaderItem title="About" iconName="user-circle" onItemClick={handleAboutClick} />
                </IconWrapper>
            </RightContentWrapper>

            {/* Mobile hamburger menu */}
            <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
                <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
            </MobileMenuButton>
            <MobileMenuOverlay $open={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />
            <MobileMenuPanel $open={mobileMenuOpen}>
                {(canEdit || canEditChemGuard) && (
                    <MobileMenuItem $active={isInEditMode} onClick={() => handleMobileNav(handleEditModeToggle)}>
                        <FontAwesomeIcon icon={faPencilAlt} /> Edit Mode {isInEditMode ? '(On)' : '(Off)'}
                    </MobileMenuItem>
                )}
                <MobileMenuDivider />
                <MobileMenuItem $active={location.pathname === '/products'} onClick={() => handleMobileNav(handleProductsClick)}>
                    <FontAwesomeIcon icon={faBox} /> Products
                </MobileMenuItem>
                <MobileMenuItem $active={location.pathname === '/documents'} onClick={() => handleMobileNav(handleDocumentsClick)}>
                    <FontAwesomeIcon icon={faFileAlt} /> Documents
                </MobileMenuItem>
                <MobileMenuItem $active={location.pathname === '/tools'} onClick={() => handleMobileNav(handleToolsClick)}>
                    <FontAwesomeIcon icon={faWrench} /> Tools
                </MobileMenuItem>
                {(canEdit || canEditChemGuard) && (
                    <MobileMenuItem $active={location.pathname === '/changelog'} onClick={() => handleMobileNav(handleChangeLogClick)}>
                        <FontAwesomeIcon icon={faHistory} /> Change Log
                    </MobileMenuItem>
                )}
                {(canEdit || canEditChemGuard) && (
                    <MobileMenuItem $active={location.pathname === '/admin'} onClick={() => handleMobileNav(handleAdminClick)}>
                        <FontAwesomeIcon icon={faUserShield} /> Admin
                    </MobileMenuItem>
                )}
                <MobileMenuDivider />
                <MobileMenuItem onClick={() => handleMobileNav(handleMyAppsClick)}>
                    <FontAwesomeIcon icon={faTh} /> My Apps
                </MobileMenuItem>
                <MobileMenuItem onClick={() => handleMobileNav(handleAboutClick)}>
                    <FontAwesomeIcon icon={faUserCircle} /> About
                </MobileMenuItem>
            </MobileMenuPanel>

            <SidePanelPopup
                visibleModule={visiblePopup}
                onClose={handleClosePopup}
                title={
                    visiblePopup === SidePanelModulEnum.About ? 'About' : visiblePopup === SidePanelModulEnum.AppsMenu ? 'My Odfjell Apps' : undefined
                }
            >
                {visiblePopup === SidePanelModulEnum.About && (
                    <div className={userInfoPanelClass}>
                        {/* User account info */}
                        {loggedInUser && (
                            <div className={userAccountSectionClass}>
                                <p className={userAccountNameClass}>{loggedInUser.fullName}</p>
                                <p className={userAccountEmailClass}>{loggedInUser.userName}</p>
                            </div>
                        )}

                        {/* User roles from AuthContext */}
                        {roles && roles.length > 0 && (
                            <div className={userRolesSectionClass}>
                                <h4>User Roles:</h4>
                                <div className={rolesContainerClass}>
                                    {getUniqueRoleDisplayNames(roles).map((displayName, index) => (
                                        <div key={index} className={roleItemContainerClass}>
                                            <span className={roleNameSpanClass}>{displayName}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Fallback when no roles loaded */}
                        {(!roles || roles.length === 0) && (
                            <div className={userRolesSectionClass}>
                                <h4>User Roles:</h4>
                                <p>No roles assigned</p>
                            </div>
                        )}

                        <div className={logoutSectionClass}>
                            <button className="logout" onClick={handleLogout}>
                                Sign out
                            </button>
                        </div>
                    </div>
                )}

                {visiblePopup === SidePanelModulEnum.AppsMenu && (
                    <>
                        {isAppsMenuLoading ? (
                            <div className={appsContentClass}>
                                <p>Loading apps menu...</p>
                            </div>
                        ) : appsMenu ? (
                            <div className={appsContentClass} dangerouslySetInnerHTML={{ __html: appsMenu.html }} />
                        ) : (
                            <div className={appsContentClass}>
                                <p>Failed to load apps menu. Please try again later.</p>
                            </div>
                        )}
                    </>
                )}
            </SidePanelPopup>
        </Wrapper>
    );
};

export default PageHeader;
